import type { Quote } from '@client/model';

export interface InvestmentBounds {
  min: number;
  max: number;
}

/**
 * investmentMin/investmentMax are NUMERIC NOT NULL and the bulk importer writes 0 when the
 * source CSV has no value, so a non-positive bound means "unknown", never "unbounded".
 * Returns undefined for unknown bounds so callers can refuse to extrapolate.
 */
export function toKnownInvestmentBound(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

/**
 * Extracts investment min values from quotes, filtering out invalid values
 */
export function extractInvestmentMins(quotes: Quote[]): number[] {
  return quotes
    .map(q => toKnownInvestmentBound(q.investmentMin))
    .filter((value): value is number => value !== undefined);
}

/**
 * Extracts investment max values from quotes, filtering out invalid values
 */
export function extractInvestmentMaxs(quotes: Quote[]): number[] {
  return quotes
    .map(q => toKnownInvestmentBound(q.investmentMax))
    .filter((value): value is number => value !== undefined);
}

/**
 * Nearest-rank percentile: returns an actual member of `values`, never a blend of two.
 *
 * Interpolating between ranks would let a single extreme value contribute its magnitude to
 * the result, with the weight growing as the sample shrinks — an interpolated p95 over one
 * $3.4M row and nine $200k rows still returns $1.96M, which is the very range this is meant
 * to exclude. Rounding the rank instead discards the outlier outright at any sample size.
 *
 * `rounding` picks which way to break a fractional rank: 'down' for an upper bound (stay
 * below the high tail), 'up' for a lower bound (stay above the low tail).
 */
export function percentile(
  values: number[],
  fraction: number,
  rounding: 'down' | 'up' = 'down',
): number | undefined {
  if (values.length === 0) return undefined;

  const sorted = [...values].sort((a, b) => a - b);
  const rank = (sorted.length - 1) * Math.min(Math.max(fraction, 0), 1);

  return sorted[rounding === 'down' ? Math.floor(rank) : Math.ceil(rank)];
}

/**
 * Calculates the most restrictive investment bounds from a set of quotes
 * Returns the highest minimum and lowest maximum to satisfy all quotes
 */
export function calculateInvestmentBounds(
  quotes: Quote[],
  fallbackBounds: InvestmentBounds = { min: 10000, max: 200000 },
): InvestmentBounds {
  const investmentMins = extractInvestmentMins(quotes);
  const investmentMaxs = extractInvestmentMaxs(quotes);

  if (investmentMins.length === 0 || investmentMaxs.length === 0) {
    return fallbackBounds;
  }

  return {
    min: Math.max(...investmentMins),
    max: Math.min(...investmentMaxs),
  };
}

